/**
 * Plugins state for Settings: the catalog, the user's installations and one
 * package's detail, each loaded and retried on its own, plus the remove,
 * update and export actions that refresh what they change.
 */
import { useCallback, useState } from "react";
import type { ApiError } from "@/api/client";
import {
  exportPlugin,
  getPluginDetail,
  listPluginCatalog,
  listPluginInstallations,
  removePlugin,
  upgradePlugin,
  type PluginDetail,
  type PluginInstallation,
  type PluginSummary,
} from "@/api/plugins";
import {
  useRetryableLoad,
  type RetryableLoad,
} from "@/hooks/use-retryable-load";
import { translate } from "@/i18n";
import { settingsPluginsMessages } from "@/i18n/messages/settings-plugins";
import { saveFile } from "@/lib/save-file";

const NO_CATALOG: PluginSummary[] = [];
const NO_INSTALLATIONS: PluginInstallation[] = [];

/** A package version the catalog no longer bundles answers 404. */
function isNotBundled(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as Partial<ApiError>).status === 404
  );
}

export function usePluginCatalog(): RetryableLoad<PluginSummary[]> {
  return useRetryableLoad(listPluginCatalog, NO_CATALOG);
}

export function usePluginInstallations(): RetryableLoad<PluginInstallation[]> {
  return useRetryableLoad(listPluginInstallations, NO_INSTALLATIONS);
}

/** The pinned version's detail; `unavailable` when it is no longer bundled. */
export function usePluginDetail(
  slug: string,
  version: string,
): RetryableLoad<PluginDetail | null> & { unavailable: boolean } {
  const load = useCallback(
    () =>
      getPluginDetail(slug, version).then(
        (detail) => ({ detail }),
        (error: unknown) => {
          if (isNotBundled(error)) return { detail: null };
          throw error;
        },
      ),
    [slug, version],
  );
  const state = useRetryableLoad<{ detail: PluginDetail | null } | null>(
    load,
    null,
  );
  return {
    ...state,
    data: state.data?.detail ?? null,
    unavailable: state.data !== null && state.data.detail === null,
  };
}

export type PluginAction = "remove" | "update" | "export";

const ACTION_ERROR_KEY = {
  remove: "errorRemove",
  update: "errorUpdate",
  export: "errorExport",
} as const satisfies Record<PluginAction, string>;

/** Package actions; each resolves true on success and reports failure inline. */
export function usePluginActions({
  slug,
  version,
  name,
  onChanged,
}: {
  slug: string;
  version: string;
  name: string;
  /** Reloads the detail and installations after a change. */
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<PluginAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function run(
    action: PluginAction,
    work: () => Promise<unknown>,
    target = version,
  ) {
    setBusy(action);
    setError(null);
    try {
      await work();
      return true;
    } catch (failure) {
      setError(
        isNotBundled(failure) && action !== "remove"
          ? translate(settingsPluginsMessages, "errorNoLongerAvailable", {
              name,
              version: target,
            })
          : translate(settingsPluginsMessages, ACTION_ERROR_KEY[action], {
              name,
            }),
      );
      return false;
    } finally {
      setBusy(null);
    }
  }
  return {
    busy,
    error,
    remove: () =>
      run("remove", async () => {
        await removePlugin(slug);
        onChanged();
      }),
    upgrade: (target: string) =>
      run("update", () => upgradePlugin(slug, target), target),
    exportRecipe: () =>
      run("export", async () => {
        const markdown = await exportPlugin(slug, version);
        await saveFile(
          new Blob([markdown], { type: "text/markdown;charset=utf-8" }),
          `${slug}-${version}.md`,
          "md",
        );
      }),
  };
}
